import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { OPCIONES_TIPO_SALIDA } from "./salidasRangos";
import DocumentosDeLaSalida from "./DocumentosDeLaSalida";

const num = (v) => parseInt(String(v ?? "").replace(/\D/g, ""), 10);
const pad = (n, ref) => String(n).padStart(String(ref ?? "").replace(/\D/g, "").length, "0");

const ESTATUS = {
  aprobado: { texto: "Aceptada", fondo: "#ECFDF5", color: "#065F46", borde: "#A7F3D0" },
  rechazado: { texto: "Rechazada", fondo: "#FEF2F2", color: "#DC2626", borde: "#FECACA" },
  pendiente: { texto: "En revisión", fondo: "#FFFBEB", color: "#B45309", borde: "#FDE68A" }
};

const campo = { width: "100%", padding: "6px 8px", borderRadius: 6, border: "1.5px solid #D8B4FE", fontSize: "12.5px", fontWeight: 600, background: "#fff", boxSizing: "border-box" };
const etiqueta = { display: "block", fontSize: "11px", fontWeight: 700, color: "#6B21A8", marginBottom: 2 };

/**
 * Reparte un rango de hologramas en varias salidas (cada una con su sub-rango, tipo opcional, almacén destino y observaciones).
 * - `rango`: { prefijo, rango1, rango2 } del rango padre (las salidas deben caer dentro).
 * - `salidas`: lista actual; `onChange(nuevaLista)` la devuelve editada.
 * - `almacenes`: lista de instalaciones/almacenes dados de alta del asociado.
 * - Una salida aceptada solo permite cambiar tipo, destino y observaciones.
 */
const SalidasDelRango = ({ rango = {}, salidas = [], almacenes: propAlmacenes, onChange, onOpenDocsSalida = null, disabled = false }) => {
  const rIni = num(rango.rango1 ?? rango.rango_inicial);
  const rFin = num(rango.rango2 ?? rango.rango_final);
  const prefijo = rango.prefijo || "A";
  const rangoValido = !isNaN(rIni) && !isNaN(rFin) && rFin >= rIni;
  const totalRango = rangoValido ? rFin - rIni + 1 : 0;

  // Catálogo de almacenes/instalaciones desde Redux como respaldo seguro
  const reduxBitacoraInst = useSelector(state => state.bitacoraProduccion?.instalaciones || []);
  const reduxFC36Inst = useSelector(state => state.FC_36Beneficiario?.instalacionesCliente || []);
  const reduxFC35Inst = useSelector(state => state.FC_35Beneficiario?.instalacionesCliente || []);
  const reduxSolicitudesInst = useSelector(state => state.Solicitudes?.instalacionesCliente || []);
  const reduxInstalacionInst = useSelector(state => state.Instalacion?.instalacionesCliente || []);

  const almacenes = useMemo(() => {
    if (Array.isArray(propAlmacenes) && propAlmacenes.length > 0) {
      return propAlmacenes;
    }
    const list = [
      ...(Array.isArray(reduxBitacoraInst) ? reduxBitacoraInst : []),
      ...(Array.isArray(reduxFC36Inst) ? reduxFC36Inst : []),
      ...(Array.isArray(reduxFC35Inst) ? reduxFC35Inst : []),
      ...(Array.isArray(reduxSolicitudesInst) ? reduxSolicitudesInst : []),
      ...(Array.isArray(reduxInstalacionInst) ? reduxInstalacionInst : [])
    ];
    const map = new Map();
    list.forEach(item => {
      if (!item) return;
      const idAnexo = item.idanexocliente || item.id_anexo_cliente || item.id || item.id_unidad_produccion;
      if (!idAnexo || map.has(String(idAnexo))) return;
      const infoGen = item.informaciongeneralinstalacioncliente || item;
      const alias = infoGen?.alias || item.alias || `Instalación #${idAnexo}`;
      const tipo = item.tipo || infoGen?.tipo || (item.idtipoanexo === 3 ? "Unidad de almacén" : item.idtipoanexo === 2 ? "Unidad de envasado" : "Instalación");
      let dir = infoGen?.direccion || item.direccion;
      if (!dir && infoGen) {
        dir = [infoGen.calle, infoGen.numeroexterior || infoGen.numext, infoGen.colonia, infoGen.municipio, infoGen.estado].filter(Boolean).join(", ");
      }
      map.set(String(idAnexo), {
        id: idAnexo,
        idanexocliente: idAnexo,
        alias,
        tipo,
        direccion: dir,
        idtipoanexo: item.idtipoanexo
      });
    });
    const all = Array.from(map.values());
    const soloAlmacen = all.filter(i => {
      const idTipo = Number(i.idtipoanexo);
      const t = String(i.tipo || "").toLowerCase();
      const al = String(i.alias || "").toLowerCase();
      return idTipo === 3 || t.includes("almac") || al.includes("almac") || t.includes("bodega") || al.includes("bodega");
    });
    return soloAlmacen.length > 0 ? soloAlmacen : all;
  }, [propAlmacenes, reduxBitacoraInst, reduxFC36Inst, reduxFC35Inst, reduxSolicitudesInst, reduxInstalacionInst]);

  const vigentes = salidas.filter(s => String(s.estatus || "").toLowerCase() !== "rechazado");
  const asignados = vigentes.reduce((acc, s) => acc + (parseInt(s.cantidad, 10) || 0), 0);

  const actualizar = (idx, cambios) => {
    onChange(salidas.map((s, i) => (i === idx ? { ...s, ...cambios } : s)));
  };

  const cambiarCantidad = (idx, valor) => {
    const s = salidas[idx];
    const cant = parseInt(valor, 10);
    const cambios = { cantidad: valor };
    const ini = num(s.rango1);
    if (!isNaN(cant) && cant > 0 && !isNaN(ini)) cambios.rango2 = pad(ini + cant - 1, s.rango1);
    actualizar(idx, cambios);
  };

  const cambiarIni = (idx, valor) => {
    const s = salidas[idx];
    const ini = num(valor);
    const cant = parseInt(s.cantidad, 10);
    const cambios = { rango1: valor.replace(/\D/g, "") };
    if (!isNaN(ini) && !isNaN(cant) && cant > 0) cambios.rango2 = pad(ini + cant - 1, valor);
    actualizar(idx, cambios);
  };

  const cambiarFin = (idx, valor) => {
    const s = salidas[idx];
    const fin = num(valor);
    const ini = num(s.rango1);
    const cambios = { rango2: valor.replace(/\D/g, "") };
    if (!isNaN(fin) && !isNaN(ini) && fin >= ini) cambios.cantidad = String(fin - ini + 1);
    actualizar(idx, cambios);
  };

  const agregar = () => {
    // El siguiente folio libre dentro del rango (después de la última salida vigente)
    const ocupados = vigentes.map(s => num(s.rango2)).filter(n => !isNaN(n));
    const siguiente = ocupados.length ? Math.max(...ocupados) + 1 : rIni;
    onChange([
      ...salidas,
      {
        local_id: `n-${Date.now()}-${salidas.length}`,
        tipo_salida: "",
        almacen_destino: "",
        id_instalacion_destino: null,
        prefijo,
        rango1: !isNaN(siguiente) && siguiente <= rFin ? pad(siguiente, rango.rango1) : "",
        rango2: "",
        cantidad: "",
        observaciones: ""
      }
    ]);
  };

  const quitar = (idx) => onChange(salidas.filter((_, i) => i !== idx));

  const problema = (s) => {
    const a = num(s.rango1);
    const b = num(s.rango2);
    const cant = parseInt(s.cantidad, 10) || 0;
    if (cant <= 0 && isNaN(a) && isNaN(b)) return null; // fila vacía: no se envía
    if (isNaN(a) || isNaN(b)) return "Indique el folio inicial y la cantidad (o el folio final).";
    if (b < a) return "El folio final es menor al inicial.";
    if (rangoValido && (a < rIni || b > rFin)) return `Debe estar dentro de ${prefijo}${rango.rango1} - ${prefijo}${rango.rango2}.`;
    if (/almac[eé]n/i.test(String(s.tipo_salida || "")) && !String(s.almacen_destino || "").trim()) {
      return "Seleccione el almacén de destino para esta salida.";
    }
    const otra = vigentes.find(o => o !== s && !isNaN(num(o.rango1)) && !isNaN(num(o.rango2)) && a <= num(o.rango2) && num(o.rango1) <= b);
    if (otra) return `Se traslapa con ${prefijo}${otra.rango1} - ${prefijo}${otra.rango2}.`;
    return null;
  };

  return (
    <div style={{ marginTop: 12, padding: "10px 12px", background: "#FAF5FF", border: "1px dashed #D8B4FE", borderRadius: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: salidas.length ? 8 : 0 }}>
        <div style={{ fontSize: "12.5px", fontWeight: 800, color: "#6B21A8" }}>
          Salidas de este rango <span style={{ fontWeight: 600, color: "#7C3AED" }}>(opcional)</span>
          {rangoValido && (
            <span style={{ marginLeft: 8, fontWeight: 600, color: asignados > totalRango ? "#DC2626" : "#64748B" }}>
              {asignados} de {totalRango} folios con salida
            </span>
          )}
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={agregar}
            disabled={!rangoValido}
            title={rangoValido ? "Reparte una parte de los folios de este rango en una salida" : "Primero capture el rango de hologramas"}
            style={{ padding: "4px 10px", background: "#fff", color: "#6B21A8", border: "1px solid #D8B4FE", borderRadius: 6, fontSize: "11.5px", fontWeight: 700, cursor: rangoValido ? "pointer" : "not-allowed", opacity: rangoValido ? 1 : 0.5 }}
          >
            + Agregar salida
          </button>
        )}
      </div>

      {salidas.map((s, idx) => {
        const estatus = String(s.estatus || "").toLowerCase();
        const aprobada = estatus === "aprobado";
        const est = ESTATUS[estatus];
        const bloqueaFolios = disabled || aprobada;
        const error = problema(s);
        const esSalidaAlmacen = /almac[eé]n/i.test(String(s.tipo_salida || ""));

        return (
          <div key={s.id_salida || s.local_id || idx} style={{ background: "#fff", border: `1px solid ${error ? "#FECACA" : "#E9D5FF"}`, borderRadius: 8, padding: 8, marginBottom: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(150px, 2fr) minmax(70px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) auto", gap: 8, alignItems: "end" }}>
              <label style={etiqueta}>Tipo de salida (opcional)
                <select value={s.tipo_salida || ""} disabled={disabled} onChange={e => actualizar(idx, { tipo_salida: e.target.value })} style={campo}>
                  <option value="">-- Sin definir --</option>
                  {OPCIONES_TIPO_SALIDA.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </label>
              <label style={etiqueta}>Cantidad
                <input type="number" min="1" value={s.cantidad ?? ""} readOnly={bloqueaFolios} onChange={e => cambiarCantidad(idx, e.target.value)} style={campo} />
              </label>
              <label style={etiqueta}>Folio inicial
                <input type="text" value={s.rango1 ?? ""} readOnly={bloqueaFolios} onChange={e => cambiarIni(idx, e.target.value)} style={campo} />
              </label>
              <label style={etiqueta}>Folio final
                <input type="text" value={s.rango2 ?? ""} readOnly={bloqueaFolios} onChange={e => cambiarFin(idx, e.target.value)} style={campo} />
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 4 }}>
                {est && (
                  <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "10.5px", fontWeight: 700, background: est.fondo, color: est.color, border: `1px solid ${est.borde}` }}>{est.texto}</span>
                )}
                {!disabled && !aprobada && (
                  <button type="button" onClick={() => quitar(idx)} title="Quitar esta salida" style={{ padding: "2px 8px", background: "#fff", color: "#DC2626", border: "1px solid #FECACA", borderRadius: 6, fontSize: "12px", cursor: "pointer" }}>✕</button>
                )}
              </div>
            </div>

            {/* SELECTOR DE ALMACÉN DADO DE ALTA CUANDO SE SELECCIONA SALIDA A OTRO ALMACÉN */}
            {esSalidaAlmacen && (
              <div style={{ marginTop: 8, padding: "8px 10px", background: "#F5F3FF", border: "1.5px solid #DDD6FE", borderRadius: 8 }}>
                <label style={{ ...etiqueta, color: "#6D28D9", display: "flex", alignItems: "center", gap: 5 }}>
                  <span>🏪 Almacén de destino dado de alta *</span>
                  <span style={{ fontSize: "10px", fontWeight: 600, color: "#7C3AED" }}>({almacenes.length} registrados)</span>
                </label>
                <select
                  value={s.id_instalacion_destino || s.almacen_destino || ""}
                  disabled={disabled || aprobada}
                  onChange={e => {
                    const selVal = e.target.value;
                    const found = almacenes.find(a => String(a.idanexocliente || a.id) === String(selVal) || String(a.alias) === String(selVal));
                    const aliasFinal = found ? (found.alias || found.informaciongeneralinstalacioncliente?.alias) : selVal;
                    const idFinal = found ? (found.idanexocliente || found.id) : null;
                    actualizar(idx, {
                      id_instalacion_destino: idFinal,
                      almacen_destino: aliasFinal
                    });
                  }}
                  style={{ ...campo, borderColor: !s.almacen_destino ? "#DC2626" : "#A78BFA", background: "#fff", fontWeight: 600 }}
                >
                  <option value="">-- Seleccionar almacén dado de alta ({almacenes.length} disponibles) --</option>
                  {almacenes.map((alm, aIdx) => {
                    const idKey = alm.idanexocliente || alm.id || aIdx;
                    const label = alm.alias || alm.informaciongeneralinstalacioncliente?.alias || `Almacén #${idKey}`;
                    const dir = alm.direccion || alm.informaciongeneralinstalacioncliente?.direccion || "";
                    const tipoLabel = alm.tipo || (alm.idtipoanexo === 3 ? "Unidad de almacén" : "Instalación");
                    return (
                      <option key={idKey} value={idKey}>
                        [{tipoLabel}] {label} {dir ? `— ${dir}` : ""}
                      </option>
                    );
                  })}
                </select>
                {s.almacen_destino ? (
                  <div style={{ marginTop: 4, fontSize: "11px", color: "#6D28D9", fontWeight: 600 }}>
                    ✓ Destino seleccionado: <strong>{s.almacen_destino}</strong> (al ser dictaminada y aprobada por UI, ingresará automáticamente a la Bitácora de Almacén en Granel / Producto Terminado).
                  </div>
                ) : (
                  <div style={{ marginTop: 4, fontSize: "11px", color: "#DC2626", fontWeight: 600 }}>
                    ⚠️ Seleccione el almacén receptor de este producto terminado.
                  </div>
                )}
              </div>
            )}

            <label style={{ ...etiqueta, marginTop: 6 }}>Observaciones de la salida (opcional)
              <textarea value={s.observaciones || ""} disabled={disabled} rows={1} onChange={e => actualizar(idx, { observaciones: e.target.value })} style={{ ...campo, resize: "vertical", fontWeight: 500 }} />
            </label>
            {error && <div style={{ marginTop: 4, fontSize: "11.5px", fontWeight: 600, color: "#DC2626" }}>⚠️ {error}</div>}
            {estatus === "rechazado" && s.observaciones_correccion && (
              <div style={{ marginTop: 4, fontSize: "11.5px", color: "#DC2626" }}><strong>Observación del inspector:</strong> {s.observaciones_correccion}</div>
            )}

            {/* DOCUMENTOS EXCLUSIVOS DE ESTA SALIDA (Diferenciados de los documentos del rango) */}
            <DocumentosDeLaSalida
              salida={s}
              rango={rango}
              onChange={nextSal => actualizar(idx, { documentosPendientes: nextSal.documentosPendientes })}
              onOpenModal={onOpenDocsSalida ? (sal) => onOpenDocsSalida(sal, rango, salidas) : null}
              origen="salida_holograma"
            />
          </div>
        );
      })}
    </div>
  );
};

export default SalidasDelRango;
